import {
    buildAircraftNeeds,
    buildCrdLifeIndex,
    buildFleetIndex,
    donorComponentLifeStatus,
    formatDonorLifeDisplay,
    isDonorForPart,
    lookupCrdLife,
    lookupCrdLifeAtSite,
    lookupPartType,
    resolveNeedProfile,
    scoreDonor,
} from './cannibalizationUtils';
import { businessDaysBetween, computeSequentialEcd, normalizePartNo } from './ltdEcdUtils';

const partKey = (site, buno, partNorm) => `${site}::${buno}::${partNorm}`;

const parseDateOnly = (value) => {
    if (!value) return null;
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
    if (!match) return null;
    const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
    return Number.isNaN(date.getTime()) ? null : date;
};

const daysDownFromLfd = (lastFlyByBuno, buno, modex, today) => {
    const lfd = parseDateOnly(lastFlyByBuno?.[buno] || lastFlyByBuno?.[modex] || null);
    if (!lfd) return null;
    return Math.max(0, Math.floor((today.getTime() - lfd.getTime()) / 86400000));
};

const withPartsAvailable = (parts, partNorms, today) => parts.map(part => (
    partNorms.has(normalizePartNo(part.partNo))
        ? { ...part, availableOn: today }
        : part
));

const compareDesc = (a, b) => {
    if (a == null && b == null) return 0;
    if (a == null) return 1;
    if (b == null) return -1;
    return b - a;
};

const donorsForPart = ({
    site,
    partNorm,
    fleet,
    needsByAircraft,
    crdLifeByKey,
    lastFlyByBuno,
    today,
}) => fleet
    .filter(donor => isDonorForPart(donor, partNorm, needsByAircraft, crdLifeByKey, today))
    .map(donor => {
        const needAc = resolveNeedProfile(donor, needsByAircraft);
        const daysDown = daysDownFromLfd(lastFlyByBuno, donor.buno, donor.modex, today);
        const partLife = lookupCrdLife(crdLifeByKey, site, donor.buno, partNorm);
        const lifeDisplay = formatDonorLifeDisplay(partLife);
        return {
            ...donor,
            daysDown,
            ak0Count: needAc?.ak0Count || 0,
            donorLifeStr: lifeDisplay.summary,
            donorScore: scoreDonor({ ...donor, daysDown }, needsByAircraft, partLife, today),
        };
    })
    .sort((a, b) => b.donorScore - a.donorScore);

/**
 * Assign one donor per part number to the LTD pull that saves the most time.
 * Savings are measured on the LTD crew schedule, not calendar days until EDD.
 */
export function assignLtdCannibalizations({
    ltdAircraft,
    ak0Rows,
    fleetAircraft,
    crdRows,
    lastFlyByBuno = {},
    today,
    siteFilter = 'ALL',
}) {
    const needsByAircraft = buildAircraftNeeds(ak0Rows, siteFilter);
    const { bySite } = buildFleetIndex(fleetAircraft, siteFilter);
    const crdLifeByKey = buildCrdLifeIndex(crdRows, siteFilter);
    const candidates = [];

    for (const aircraft of ltdAircraft || []) {
        const fleet = bySite.get(aircraft.site) || [];
        const baseline = computeSequentialEcd(aircraft.parts, today);
        for (const part of aircraft.parts) {
            const partNorm = normalizePartNo(part.partNo);
            if (!partNorm || lookupPartType(part.partNo) !== 'Repairable') continue;
            const donors = donorsForPart({
                site: aircraft.site,
                partNorm,
                fleet,
                needsByAircraft,
                crdLifeByKey,
                lastFlyByBuno,
                today,
            });
            if (donors.length === 0) continue;

            const pulled = computeSequentialEcd(
                withPartsAvailable(aircraft.parts, new Set([partNorm]), today),
                today,
            );
            const basePart = baseline.parts.find(row => normalizePartNo(row.partNo) === partNorm);
            const pullPart = pulled.parts.find(row => normalizePartNo(row.partNo) === partNorm);
            const partDaysSaved = businessDaysBetween(pullPart?.partEcd, basePart?.partEcd);
            const aircraftDaysSaved = businessDaysBetween(pulled.finalEcd, baseline.finalEcd);
            const clearsUnknown = baseline.hasUnknownEcd && !pulled.hasUnknownEcd;
            const priorityBoost = clearsUnknown || (!part.edd && pullPart?.partEcd);
            if (!priorityBoost && !(partDaysSaved > 0) && !(aircraftDaysSaved > 0)) continue;

            candidates.push({
                site: aircraft.site,
                buno: aircraft.buno,
                aircraftKey: aircraft.aircraftKey,
                partNorm,
                partNo: part.partNo,
                donors,
                donorScore: donors[0].donorScore,
                partDaysSaved,
                aircraftDaysSaved,
                clearsUnknown,
                priorityBoost,
            });
        }
    }

    candidates.sort((a, b) => {
        if (a.priorityBoost !== b.priorityBoost) return a.priorityBoost ? -1 : 1;
        const byAircraft = compareDesc(a.aircraftDaysSaved, b.aircraftDaysSaved);
        if (byAircraft) return byAircraft;
        const byPart = compareDesc(a.partDaysSaved, b.partDaysSaved);
        if (byPart) return byPart;
        return b.donorScore - a.donorScore;
    });

    const usedDonors = new Set();
    const byPartKey = new Map();
    const assignedNorms = new Map();

    for (const candidate of candidates) {
        const donor = candidate.donors.find(row => (
            !usedDonors.has(`${candidate.site}::${candidate.partNorm}::${row.key}`)
        ));
        if (!donor) continue;
        const siteLife = lookupCrdLifeAtSite(
            crdLifeByKey,
            donor.site,
            donor.buno,
            candidate.partNorm,
        );
        const verified = donorComponentLifeStatus(siteLife, today) === 'usable';
        usedDonors.add(`${candidate.site}::${candidate.partNorm}::${donor.key}`);
        byPartKey.set(partKey(candidate.site, candidate.buno, candidate.partNorm), {
            donorBuno: donor.buno,
            donorAk0Count: donor.ak0Count,
            donorLifeStr: verified
                ? donor.donorLifeStr
                : (siteLife
                    ? 'CRD shows this component installed; remaining life still needs verification'
                    : 'No CRD confirmation at this site; donor is not confirmed'),
            partDaysSaved: candidate.partDaysSaved,
            aircraftDaysSaved: candidate.aircraftDaysSaved,
            clearsUnknown: candidate.clearsUnknown,
            verified,
        });
        if (!verified) continue;
        const norms = assignedNorms.get(candidate.aircraftKey) || new Set();
        norms.add(candidate.partNorm);
        assignedNorms.set(candidate.aircraftKey, norms);
    }

    const byAircraftKey = new Map();
    for (const aircraft of ltdAircraft || []) {
        const norms = assignedNorms.get(aircraft.aircraftKey);
        if (!norms || norms.size === 0) continue;
        const baseline = computeSequentialEcd(aircraft.parts, today);
        const joint = computeSequentialEcd(withPartsAvailable(aircraft.parts, norms, today), today);
        const bothKnown = !baseline.hasUnknownEcd && !joint.hasUnknownEcd;
        byAircraftKey.set(aircraft.aircraftKey, {
            cannibalizeEcd: joint.hasUnknownEcd ? null : joint.finalEcd,
            cannibalizeHasUnknown: joint.hasUnknownEcd,
            aircraftDaysSaved: bothKnown
                ? businessDaysBetween(joint.finalEcd, baseline.finalEcd)
                : null,
        });
    }

    return { byPartKey, byAircraftKey };
}
